import { AlignLeft, Check, FileText, FolderOpen, Globe, Plus, Youtube, Lock } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IntercomIcon,
  ShopifyIcon,
  type AppIcon,
} from '@/Domain/components/Shared/BrandIcon/BrandIcon';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { UpgradePlanButton } from '@/Domain/components/UpgradePlanButton/UpgradePlanButton';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useShopifyConnection } from '@/Domain/hooks/Action/ShopifyMcp/useShopifyConnection.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useArticleLimitCheck } from '@/Domain/hooks/KnowledgeBase/useArticleLimitCheck.hook';
import { useCreateArticle } from '@/Domain/hooks/KnowledgeBase/useCreateArticle.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type CardConfig = {
  label: string;
  lucideIcon?: AppIcon;
  onClick: () => void;
  dataTestId: string;
  disabled?: boolean;
  connected?: boolean;
  // Shopify MCP is an AI Action, not a KB article, so it must stay reachable even
  // when the article limit is hit (and not look dimmed/blocked).
  bypassLimit?: boolean;
};

export const AddContentCards = () => {
  const { t } = useTranslation('knowledge_base');
  const { toggleModal } = useModalStore();
  const { isConnected: isShopifyConnected } = useShopifyConnection();
  const { handleCreateArticle, isPending } = useCreateArticle();
  const handleArticleLimitCheck = useArticleLimitCheck();
  const { getConfig, isFetched } = useSubscription();
  const { capture } = usePostHogAnalytics();
  const canAdd = getConfig('can_add_article') as boolean | undefined;
  const articlesLimit = getConfig('articles_limit') as number | null | undefined;
  const articlesUsed = getConfig('articles_used') as number | undefined;
  const atLimit = isFetched && canAdd === false;

  const wasAtLimit = useRef(false);
  useEffect(() => {
    if (atLimit && !wasAtLimit.current) {
      capture(PostHogEventEnum.KbLimitBannerViewed, {
        articles_used: articlesUsed,
        articles_limit: articlesLimit,
      });
    }
    wasAtLimit.current = atLimit;
  }, [atLimit, articlesUsed, articlesLimit, capture]);

  // Gate every add action through the limit check — the modal is the
  // friendly way to tell the user they're capped, vs the user clicking
  // through several modals only to be rejected at submit time.
  const gated = (action: () => void) => () => handleArticleLimitCheck(action);

  const handleCreateArticleClick = gated(() => {
    handleCreateArticle({ title: '', content: '', url: '', tags: [], itemsCount: 0 });
  });

  const cards: CardConfig[] = [
    {
      label: t('add_content.create_article'),
      lucideIcon: FileText,
      onClick: handleCreateArticleClick,
      dataTestId: 'add-content-article',
      disabled: isPending,
    },
    {
      label: t('add_content.create_paragraph'),
      lucideIcon: AlignLeft,
      onClick: gated(() => toggleModal(MODAL_NAMES.PARAGRAPH_SHEET)),
      dataTestId: 'add-content-paragraph',
    },
    {
      label: t('add_content.add_url'),
      lucideIcon: Globe,
      onClick: gated(() => toggleModal(MODAL_NAMES.UPLOAD_URL_MODAL_ID)),
      dataTestId: 'add-content-url',
    },
    {
      label: t('add_content.add_file'),
      lucideIcon: FolderOpen,
      onClick: gated(() => toggleModal(MODAL_NAMES.UPLOAD_MODAL_ID)),
      dataTestId: 'add-content-file',
    },
    {
      label: t('add_content.shopify'),
      lucideIcon: ShopifyIcon,
      onClick: () => toggleModal(MODAL_NAMES.SHOPIFY_CONNECT_MODAL_ID),
      dataTestId: 'add-content-shopify',
      connected: isShopifyConnected,
      bypassLimit: true,
    },
    {
      label: t('add_content.intercom'),
      lucideIcon: IntercomIcon,
      onClick: gated(() => toggleModal(MODAL_NAMES.INTERCOM_ARTICLES_MODAL_ID)),
      dataTestId: 'add-content-intercom',
    },
    {
      label: t('add_content.youtube'),
      lucideIcon: Youtube,
      onClick: gated(() => toggleModal(MODAL_NAMES.UPLOAD_YOUTUBE_MODAL_ID)),
      dataTestId: 'add-content-youtube',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {atLimit && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-warning/20 bg-warning-muted px-4 py-2.5 text-sm text-warning"
          data-testid="kb-articles-at-limit-banner"
        >
          <div className="flex min-w-0 items-start gap-2 sm:items-center">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning sm:mt-0" />
            {/* No `truncate` here — let the message wrap to a second line on
                mobile so the Upgrade button can keep its full label. */}
            <span className="min-w-0">
              {articlesLimit
                ? t('add_content.at_limit_with_count', { limit: articlesLimit.toLocaleString() })
                : t('add_content.at_limit')}
            </span>
          </div>
          <UpgradePlanButton size="xs" source="kb_add_content_banner" className="shrink-0" />
        </div>
      )}

      <div className="-mx-6 flex gap-3 overflow-x-auto px-6 pb-2 sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border">
        {cards.map(
          ({
            label,
            lucideIcon: LucideIcon,
            onClick,
            dataTestId,
            disabled,
            connected,
            bypassLimit,
          }) => (
            <button
              key={dataTestId}
              data-testid={dataTestId}
              onClick={onClick}
              disabled={disabled}
              className={`flex min-h-[5.5rem] w-[7.5rem] shrink-0 cursor-pointer flex-col gap-2 sm:h-[5.5rem] justify-between rounded-2xl border border-border bg-card p-3.5 text-left transition-colors hover:border-foreground-subtle hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 ${
                atLimit && !bypassLimit ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center justify-between w-full">
                {LucideIcon ? <LucideIcon className="h-5 w-5 text-muted-foreground" /> : null}
                {connected ? (
                  <Check
                    className="h-3.5 w-3.5 text-success"
                    data-testid={`${dataTestId}-connected`}
                  />
                ) : (
                  <Plus className="h-3.5 w-3.5 text-foreground-subtle" />
                )}
              </div>
              <span className="w-full text-sm font-medium text-foreground sm:truncate">
                {label}
              </span>
            </button>
          ),
        )}
      </div>
    </div>
  );
};
